import type { User } from '@/features/users/shared/types';

export const CLIENT_CLEANER_RESTRICTION_WARNING_CODE =
  'client_cleaner_restriction';

export type AssignmentWarning = {
  code: string;
  client_user_id?: number;
  cleaner_id?: number;
  reason?: string | null;
  overridden?: boolean;
};

export type WithAssignmentWarnings<T> = T & {
  warnings?: AssignmentWarning[];
};

export const attachAssignmentWarnings = <T>(
  data: T,
  warnings?: AssignmentWarning[]
): WithAssignmentWarnings<T> => {
  if (!warnings?.length || !data || typeof data !== 'object') {
    return data as WithAssignmentWarnings<T>;
  }

  return {
    ...(data as object),
    warnings,
  } as WithAssignmentWarnings<T>;
};

export const getClientCleanerRestrictionWarnings = (
  source?: { warnings?: AssignmentWarning[] } | null
): AssignmentWarning[] =>
  (source?.warnings ?? []).filter(
    (warning) =>
      warning.code === CLIENT_CLEANER_RESTRICTION_WARNING_CODE &&
      warning.overridden === true
  );

const getCleanerDisplayName = (
  cleanerId: number | undefined,
  cleaners?: Pick<User, 'id' | 'first_name' | 'last_name' | 'email'>[]
) => {
  if (!cleanerId) return 'This cleaner';

  const cleaner = cleaners?.find(
    (option) => Number(option.id) === Number(cleanerId)
  );

  if (!cleaner) return `Cleaner #${cleanerId}`;

  const name = [cleaner.first_name, cleaner.last_name]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

  return name || cleaner.email || `Cleaner #${cleanerId}`;
};

export const buildClientCleanerRestrictionWarningMessage = (
  warning: AssignmentWarning,
  cleaners?: Pick<User, 'id' | 'first_name' | 'last_name' | 'email'>[]
) => {
  const cleanerName = getCleanerDisplayName(warning.cleaner_id, cleaners);
  const reason = warning.reason?.trim() || 'No reason provided.';

  return `${cleanerName} is restricted for this client. Reason: ${reason}`;
};

export const showClientCleanerRestrictionWarnings = (
  source: { warnings?: AssignmentWarning[] } | null | undefined,
  warningMsg: (message: string) => void,
  cleaners?: Pick<User, 'id' | 'first_name' | 'last_name' | 'email'>[]
) => {
  getClientCleanerRestrictionWarnings(source).forEach((warning) => {
    warningMsg(buildClientCleanerRestrictionWarningMessage(warning, cleaners));
  });
};
